/**
 * Ticket Lifecycle v2 — deriveTimeBuckets
 *
 * Pure function that converts a ticket plus its custody event stream
 * into a `TicketTimeBuckets` snapshot.
 *
 * Slice 1 contract (unchanged):
 *   - `executionMinutes === input.optiTime ?? 0` (anchored to the DB
 *     wall-clock value written by `canonicalTimeMath` / `reassignCore`).
 *   - `cycleMinutes` is computed from `createdAt` and the better of
 *     `terminalAt` (preferred) or `referenceTime` (fallback).
 *   - The function is pure: same inputs ⇒ same outputs. No clock reads,
 *     no environment access, no I/O.
 *
 * Slice 5A.1 widening:
 *   - When `custodyEvents` is provided, compute four pair-based
 *     custody buckets:
 *       • `fohPendingAcceptMinutes` (kick_to_foh → foh_accept)
 *       • `fohActiveMinutes`        (foh_accept  → kick_to_boh)
 *       • `bohPendingAcceptMinutes` (kick_to_boh → boh_accept)
 *       • `advisorObservedMinutes`  = sum of the three above
 *   - Derive `fohCustodyMinutes = fohPendingAcceptMinutes + fohActiveMinutes`.
 *   - Derive `bohCustodyMinutes = max(0, cycleMinutes − fohCustodyMinutes)`
 *     when `cycleMinutes > 0`; otherwise 0.
 *
 * Open-interval rule (slice 5A.1):
 *   - When `referenceTime` is provided AND the final event in the
 *     sorted stream is the pair's `fromKind`, the trailing interval
 *     `from → referenceTime` is counted. If any other event kind
 *     intervened after the last `fromKind`, the trailing interval is
 *     NOT counted (defensive against reject_back / reassign_within_*
 *     terminating a pending state).
 *
 * Multi-ticket safety:
 *   - Events are grouped by `ticket_id` internally and walked per-ticket
 *     in ascending `created_at` order. Callers may pass single-ticket or
 *     mixed-ticket arrays without unsafe cross-pairing.
 *
 * This module must not import from scheduling, timer, parking, rollover,
 * bay, or invariants modules. See docs/lifecycle/TICKET_LIFECYCLE_V2_SPEC.md
 * §9 gate #3 and docs/lifecycle/OPTITIME_LIFECYCLE_BUCKETS_PLAN.md §7.
 */

import type { CustodyEventKind, TicketTimeBuckets } from './types';

/**
 * Minimal custody event shape consumed by the pair walker. Mirrors the
 * shape used by slice-4E `advisorMetrics.ts` to keep both helpers in
 * sync if they're ever consolidated.
 */
export interface CustodyEventLite {
  event_kind: CustodyEventKind;
  ticket_id: string;
  created_at: string;
}

/**
 * Input to `deriveTimeBuckets`. Minimal shape so this function can be
 * called from server, client, or test code without dragging in the
 * full `Ticket` interface (which would imply coupling).
 *
 * Note: `optiTime` is the snake_case DB column `tickets.opti_time`
 * expressed in idiomatic TS. The TS-side `Ticket` type does not surface
 * this field (it lives only on the DB row in
 * `src/integrations/supabase/types.ts`); callers that have a DB row
 * pass `row.opti_time` directly.
 */
export interface DeriveTimeBucketsInput {
  /**
   * The canonical wall-clock scheduling duration from
   * `tickets.opti_time`. Pass `null` if not yet computed.
   */
  optiTime: number | null;

  /**
   * ISO-8601 timestamp of ticket creation (`tickets.created_at`).
   * Required for any non-zero `cycleMinutes`.
   */
  createdAt?: string | null;

  /**
   * ISO-8601 timestamp marking lifecycle end — typically the
   * `entered_at` of `ticket_business_state.current_state` once it
   * reaches a terminal value (`closed` | `voided`), or the
   * `tickets.updated_at` of a `Completed` ticket. Preferred over
   * `referenceTime` when present.
   */
  terminalAt?: string | null;

  /**
   * ISO-8601 timestamp to use as "now" for in-progress tickets. Caller
   * supplies this (typically the request's wall clock) so the function
   * remains pure. If neither `terminalAt` nor `referenceTime` is
   * supplied:
   *   - `cycleMinutes` is `0`.
   *   - Open custody intervals (e.g. a ticket currently in
   *     `pending_advisor_accept`) are NOT counted.
   */
  referenceTime?: string | null;

  /**
   * Slice 5A.1: custody events sourced from `ticket_custody_events`.
   * Optional; when omitted or empty, all custody-derived buckets
   * return 0. The walker tolerates unsorted input and multi-ticket
   * arrays (groups by ticket_id internally).
   */
  custodyEvents?: ReadonlyArray<CustodyEventLite>;
}

/**
 * Compute the `TicketTimeBuckets` snapshot. Pure, deterministic, side-
 * effect-free.
 *
 * @see TicketTimeBuckets for bucket semantics.
 * @see docs/lifecycle/TICKET_LIFECYCLE_V2_SPEC.md §3 for the original model.
 * @see docs/lifecycle/OPTITIME_LIFECYCLE_BUCKETS_PLAN.md §5 for slice 5A.1.
 */
export function deriveTimeBuckets(
  input: DeriveTimeBucketsInput,
): TicketTimeBuckets {
  const executionMinutes = clampNonNegative(input.optiTime ?? 0);
  const cycleMinutes = computeCycleMinutes(
    input.createdAt ?? null,
    input.terminalAt ?? null,
    input.referenceTime ?? null,
  );

  const events = input.custodyEvents ?? [];
  const refMs = parseRefMs(input.referenceTime);

  const fohPendingAcceptArr = pairedIntervalsMinutes(
    events,
    'kick_to_foh',
    'foh_accept',
    refMs,
  );
  const fohActiveArr = pairedIntervalsMinutes(
    events,
    'foh_accept',
    'kick_to_boh',
    refMs,
  );
  const bohPendingAcceptArr = pairedIntervalsMinutes(
    events,
    'kick_to_boh',
    'boh_accept',
    refMs,
  );

  const fohPendingAcceptMinutes = sumNonNegative(fohPendingAcceptArr);
  const fohActiveMinutes = sumNonNegative(fohActiveArr);
  const bohPendingAcceptMinutes = sumNonNegative(bohPendingAcceptArr);
  const advisorObservedMinutes =
    fohPendingAcceptMinutes + fohActiveMinutes + bohPendingAcceptMinutes;

  // FOH custody = sum of the two FOH-owned sub-buckets. Matches the
  // analytic semantics from the plan doc (§5): FOH owns the ticket from
  // `kick_to_foh` through `kick_to_boh`, broken into pending-accept and
  // active sub-windows.
  const fohCustodyMinutes = fohPendingAcceptMinutes + fohActiveMinutes;

  // BOH custody = total cycle time NOT in FOH. Requires a known cycle
  // window; otherwise 0. Clamped non-negative against any cycle <
  // foh-custody arithmetic anomaly (e.g. clock skew across distinct
  // sources).
  const bohCustodyMinutes =
    cycleMinutes > 0
      ? clampNonNegative(cycleMinutes - fohCustodyMinutes)
      : 0;

  return {
    executionMinutes,
    bayMinutes: 0,
    bohCustodyMinutes,
    fohCustodyMinutes,
    fohPendingAcceptMinutes,
    fohActiveMinutes,
    bohPendingAcceptMinutes,
    advisorObservedMinutes,
    estimateBuildMinutes: 0,
    customerWaitMinutes: 0,
    partsWaitMinutes: 0,
    idleMinutes: 0,
    cycleMinutes,
  };
}

function clampNonNegative(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return n < 0 ? 0 : n;
}

function sumNonNegative(xs: ReadonlyArray<number>): number {
  let total = 0;
  for (const x of xs) {
    if (Number.isFinite(x) && x > 0) total += x;
  }
  return total;
}

function parseMs(iso: string): number | null {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function parseRefMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function computeCycleMinutes(
  createdAt: string | null,
  terminalAt: string | null,
  referenceTime: string | null,
): number {
  if (!createdAt) return 0;
  const endIso = terminalAt ?? referenceTime;
  if (!endIso) return 0;

  const startMs = Date.parse(createdAt);
  const endMs = Date.parse(endIso);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return 0;

  const diffMinutes = (endMs - startMs) / 60_000;
  return clampNonNegative(diffMinutes);
}

/**
 * Walks custody events grouped by ticket, ascending by `created_at`,
 * and pairs each `fromKind` with the next matching `toKind`. Returns
 * the minute durations of fully-paired intervals.
 *
 * Open-interval rule: if `referenceTime` is provided AND the FINAL
 * event in the per-ticket sorted stream was the `fromKind` (i.e., no
 * other event kind intervened after it), the trailing interval is
 * counted. This guarantees we only count "pending → now" when the
 * ticket is *unambiguously* still in the pending state. A reject_back
 * or reassign_within_* between the `fromKind` and the end of stream
 * suppresses the open interval correctly.
 *
 * Pure: no clock reads, no I/O.
 */
function pairedIntervalsMinutes(
  events: ReadonlyArray<CustodyEventLite>,
  fromKind: CustodyEventKind,
  toKind: CustodyEventKind,
  referenceMs: number | null,
): number[] {
  if (events.length === 0) return [];

  // Group by ticket id; sort each group ascending by created_at.
  const byTicket = new Map<string, CustodyEventLite[]>();
  for (const ev of events) {
    if (!byTicket.has(ev.ticket_id)) byTicket.set(ev.ticket_id, []);
    byTicket.get(ev.ticket_id)!.push(ev);
  }

  const durations: number[] = [];
  for (const list of byTicket.values()) {
    const sorted = [...list].sort((a, b) =>
      a.created_at.localeCompare(b.created_at),
    );

    let lastFromMs: number | null = null;
    /** Did the most recently seen event match `fromKind`? Used to gate
     *  the open-interval count: only counts when the LAST event in the
     *  stream was the from-kind (no intervening interrupter). */
    let lastSeenWasFrom = false;

    for (const ev of sorted) {
      if (ev.event_kind === fromKind) {
        const ms = parseMs(ev.created_at);
        if (ms != null) {
          lastFromMs = ms;
          lastSeenWasFrom = true;
        } else {
          // Unparseable; treat as a reset.
          lastFromMs = null;
          lastSeenWasFrom = false;
        }
        continue;
      }

      // Any non-from event ends a "currently pending" run.
      lastSeenWasFrom = false;

      if (ev.event_kind === toKind && lastFromMs != null) {
        const toMs = parseMs(ev.created_at);
        if (toMs != null) {
          const elapsed = (toMs - lastFromMs) / 60_000;
          if (elapsed >= 0) durations.push(elapsed);
        }
        lastFromMs = null;
      }
      // Other event kinds: leave lastFromMs as-is (could pair with a
      // later toKind), but lastSeenWasFrom is already reset above so
      // an open-interval count is suppressed unless a fresh from-kind
      // re-anchors.
    }

    // Open interval: only counts if the LAST event seen was the
    // fromKind AND we have a reference. This is the conservative rule
    // — reject_back / reassign_within_* events between fromKind and
    // end-of-stream suppress the open count.
    if (lastFromMs != null && lastSeenWasFrom && referenceMs != null) {
      const elapsed = (referenceMs - lastFromMs) / 60_000;
      if (elapsed >= 0) durations.push(elapsed);
    }
  }
  return durations;
}
